<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@chatwoot/pico-search';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../SettingsLayout.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import { useChannelIconFrame } from 'next/icon/provider';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import { INBOX_TYPES, TWILIO_CHANNEL_MEDIUM } from 'dashboard/helper/inbox';
import { useAccount } from 'dashboard/composables/useAccount';
import { isApiInboxDisabledForAccount } from './apiInboxVisibility';
import InboxHealthAPI from 'dashboard/api/inboxHealth';

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { isAdmin } = useAdmin();
const { accountId } = useAccount();

const showDeletePopup = ref(false);
const deleteMode = ref('migrate');
const deleteConfirmationName = ref('');
const deleteTargetInboxId = ref('');
const showMigratePopup = ref(false);
const selectedInbox = ref({});
const migrationSourceInbox = ref({});
const migrationTargetInboxId = ref(null);
const isMigratingData = ref(false);
const isLoadingInboxes = ref(true);
const searchQuery = ref('');
const healthByInbox = ref({});
const loadedInboxIds = new Set();
const loadedHealthIds = new Set();
const failedInboxAvatarIds = ref({});

const inboxes = useMapGetter('inboxes/getInboxes');

const listCopy = {
  quality: 'Qualidade',
  connectedBot: 'Bot conectado',
  noBot: 'Sem bot',
  editBot: 'Editar bot',
  editCollaborators: 'Editar agentes e departamentos',
  settings: 'Configurar',
  manage: 'Gerenciar',
  migrateData: 'Migrar dados',
  migrateModalTitle: 'Migrar dados da caixa de entrada',
  migrateModalDescription:
    'Selecione a caixa de entrada de destino para mover contatos, conversas e mensagens.',
  migrateTargetLabel: 'Caixa de destino',
  migrateTargetPlaceholder: 'Selecione uma caixa de entrada',
  migrateConfirm: 'Migrar dados',
  migrateCancel: 'Cancelar',
  migrateSuccess: 'Migração iniciada com sucesso.',
  migrateError: 'Não foi possível iniciar a migração.',
  migrateValidationError: 'Selecione uma caixa de destino para continuar.',
  hardDeletePrompt:
    'Deseja apagar todos os contatos e mensagens desta caixa de entrada? Esta ação é permanente.',
  deleteModalTitle: 'Excluir caixa de entrada',
  deleteModalDescription:
    'Escolha como deseja excluir esta caixa. Você pode migrar os dados para outra caixa ou apagar tudo permanentemente.',
  deleteModeLabel: 'Como deseja excluir?',
  deleteModeMigrate: 'Migrar contatos e mensagens para outra caixa',
  deleteModeMigrateHint:
    'Contatos, conversas e mensagens passam para a caixa escolhida antes da exclusão.',
  deleteModeHardDelete: 'Apagar todos os contatos e mensagens',
  deleteModeHardDeleteHint:
    'Remove esta caixa e todo o histórico dela. Não é possível desfazer.',
  deleteTargetLabel: 'Caixa de destino para migração',
  deleteTargetHint:
    'Somente caixas do mesmo tipo de canal podem receber os dados.',
  deleteTargetPlaceholder: 'Selecione uma caixa de entrada',
  deleteConfirm: 'Migrar e excluir caixa',
  deleteHardConfirm: 'Apagar permanentemente',
  deleteHardWarningTitle: 'Esta ação é permanente',
  deleteHardWarning:
    'Para confirmar, digite o nome da caixa exatamente como aparece abaixo.',
  deleteHardInputLabel: 'Nome da caixa',
  deleteCancel: 'Cancelar',
  selectedInbox: 'Caixa selecionada',
  deleteValidationError:
    'Selecione uma caixa de destino para continuar com a migração.',
  incompatibleTargetError:
    'A caixa selecionada não é compatível para migração.',
  deleteScheduledSuccess:
    'Exclusão iniciada. A caixa será removida após a limpeza dos dados.',
  eyebrow: 'Central de atendimento',
  overviewTitle: 'Organize seus canais em um só lugar',
  overviewDescription:
    'Acompanhe conexões, assistentes e a qualidade dos canais sem abrir cada configuração.',
  totalConnections: 'Conexões cadastradas',
  whatsappConnections: 'Canais WhatsApp',
  connectedAssistants: 'Com assistente',
  newConnection: 'Nova conexão',
  searchLabel: 'Buscar conexões',
  channelStatus: 'Conexão cadastrada',
  openSettings: 'Configurar',
  qualityLabels: { GREEN: 'Alta', YELLOW: 'Média', RED: 'Baixa' },
  qualityUnknown: 'Qualidade não informada',
  emailReceiving: 'Recebendo e-mails',
  emailNotReceiving: 'Recebimento desativado',
};

const isPlainApiInbox = inbox =>
  inbox.channel_type === INBOX_TYPES.API &&
  !inbox.additional_attributes?.is_evolution_api_integration;

const isEvolutionApiWhatsappInbox = inbox =>
  inbox.channel_type === INBOX_TYPES.API &&
  inbox.additional_attributes?.is_evolution_api_integration;

const isTwilioWhatsappInbox = inbox =>
  inbox.channel_type === INBOX_TYPES.TWILIO &&
  inbox.medium === TWILIO_CHANNEL_MEDIUM.WHATSAPP;

const isWhatsappFamilyInbox = inbox =>
  inbox.channel_type === INBOX_TYPES.WHATSAPP ||
  isEvolutionApiWhatsappInbox(inbox) ||
  isTwilioWhatsappInbox(inbox);

const isMigrationCompatible = (sourceInbox, targetInbox) => {
  if (!sourceInbox || !targetInbox) return false;

  if (sourceInbox.channel_type === targetInbox.channel_type) return true;

  return (
    isWhatsappFamilyInbox(sourceInbox) && isWhatsappFamilyInbox(targetInbox)
  );
};

const inboxesList = computed(() => {
  const visibleInboxes = isApiInboxDisabledForAccount(accountId.value)
    ? inboxes.value?.filter(inbox => !isPlainApiInbox(inbox))
    : inboxes.value;

  return visibleInboxes?.slice().sort((a, b) => a.name.localeCompare(b.name));
});

const filteredInboxesList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return inboxesList.value;
  return picoSearch(inboxesList.value, query, [
    'name',
    'channel_type',
    'medium',
  ]);
});

const categoryLabels = {
  whatsapp: 'Conexões WhatsApp',
  instagram: 'Conexões Instagram',
  site: 'Conexões Site',
  telegram: 'Conexões Telegram',
  tiktok: 'Conexões TikTok',
  messenger: 'Conexões Messenger',
  email: 'Conexões E-mail',
  sms: 'Conexões SMS',
  voice: 'Conexões de Voz',
  api: 'Conexões API',
  line: 'Conexões Line',
  twitter: 'Conexões X',
  other: 'Outras conexões',
};

const categoryIcons = {
  whatsapp: 'i-brand-whatsapp',
  instagram: 'i-brand-instagram',
  site: 'i-lucide-panels-top-left',
  telegram: 'i-brand-telegram',
  tiktok: 'i-brand-tiktok',
  messenger: 'i-brand-messenger',
  email: 'i-lucide-mail',
  sms: 'i-lucide-message-square-text',
  voice: 'i-lucide-phone',
  api: 'i-lucide-braces',
  line: 'i-brand-line',
  twitter: 'i-brand-x',
  other: 'i-lucide-box',
};

const categoryDescriptions = {
  whatsapp: 'Canais usados para atendimento e automações no WhatsApp.',
  instagram: 'Contas conectadas para conversas originadas no Instagram.',
  site: 'Widgets e canais de chat instalados em sites.',
  telegram: 'Bots e canais conectados ao Telegram.',
  tiktok: 'Contas conectadas para interações do TikTok.',
  messenger: 'Páginas e canais conectados ao Facebook Messenger.',
  email: 'Caixas de e-mail conectadas para atendimento.',
  sms: 'Canais de SMS e números conectados.',
  voice: 'Canais habilitados para chamadas de voz.',
  api: 'Canais personalizados conectados via API.',
  line: 'Contas conectadas ao Line.',
  twitter: 'Perfis conectados ao X.',
  other: 'Canais que não se encaixam nas categorias principais.',
};

const categoryOrder = [
  'whatsapp',
  'instagram',
  'site',
  'telegram',
  'tiktok',
  'messenger',
  'email',
  'sms',
  'voice',
  'api',
  'line',
  'twitter',
  'other',
];

const getInboxCategory = inbox => {
  const isEvolutionApiInbox =
    inbox.channel_type === INBOX_TYPES.API &&
    inbox.additional_attributes?.is_evolution_api_integration;

  if (isEvolutionApiInbox) return 'whatsapp';

  if (
    inbox.channel_type === INBOX_TYPES.TWILIO &&
    inbox.medium === TWILIO_CHANNEL_MEDIUM.WHATSAPP
  ) {
    return 'whatsapp';
  }

  if (
    inbox.channel_type === INBOX_TYPES.TWILIO &&
    inbox.voice_enabled &&
    inbox.medium !== TWILIO_CHANNEL_MEDIUM.WHATSAPP
  ) {
    return 'voice';
  }

  const categoryByChannelType = {
    [INBOX_TYPES.WHATSAPP]: 'whatsapp',
    [INBOX_TYPES.INSTAGRAM]: 'instagram',
    [INBOX_TYPES.WEB]: 'site',
    [INBOX_TYPES.TELEGRAM]: 'telegram',
    [INBOX_TYPES.TIKTOK]: 'tiktok',
    [INBOX_TYPES.FB]: 'messenger',
    [INBOX_TYPES.EMAIL]: 'email',
    [INBOX_TYPES.SMS]: 'sms',
    [INBOX_TYPES.TWILIO]: 'sms',
    [INBOX_TYPES.API]: 'api',
    [INBOX_TYPES.LINE]: 'line',
    [INBOX_TYPES.TWITTER]: 'twitter',
  };

  return categoryByChannelType[inbox.channel_type] || 'other';
};

const activeBotForInbox = inbox =>
  store.getters['agentBots/getActiveAgentBot'](inbox.id) || {};

const activeBotName = inbox => activeBotForInbox(inbox).name || listCopy.noBot;

const qualityValueForInbox = inbox => {
  const healthData = healthByInbox.value[inbox.id] || {};
  const additionalAttributes = inbox.additional_attributes || {};
  const providerConfig = inbox.provider_config || {};

  return (
    healthData.quality_rating ||
    additionalAttributes.quality_rating ||
    additionalAttributes.quality ||
    providerConfig.quality_rating ||
    null
  );
};

const isWhatsAppLikeInbox = inbox => getInboxCategory(inbox) === 'whatsapp';

const qualityLabelForInbox = inbox => {
  if (!isWhatsAppLikeInbox(inbox)) return '';
  return qualityValueForInbox(inbox) || 'N/A';
};

const qualityTextForInbox = inbox =>
  `${listCopy.quality}: ${qualityLabelForInbox(inbox)}`;

const qualityClassForInbox = inbox => {
  const quality = qualityValueForInbox(inbox);
  if (quality === 'GREEN') return 'border-n-teal-7';
  if (quality === 'YELLOW') return 'border-n-amber-7';
  if (quality === 'RED') return 'border-n-ruby-7';
  return 'border-n-weak';
};

const qualityDotClass = inbox =>
  ({
    GREEN: 'bg-n-teal-9 motion-safe:animate-pulse',
    YELLOW: 'bg-n-amber-9 motion-safe:animate-pulse',
    RED: 'bg-n-ruby-9 motion-safe:animate-pulse',
  })[qualityValueForInbox(inbox)] || 'bg-n-slate-8';

const qualityBadgeForInbox = inbox => {
  if (isWhatsAppLikeInbox(inbox)) {
    const quality = qualityValueForInbox(inbox);
    const label = listCopy.qualityLabels[quality];
    return {
      label: label
        ? `${listCopy.quality} ${label.toLowerCase()}`
        : listCopy.qualityUnknown,
      tone:
        {
          GREEN: 'bg-n-teal-3 text-n-teal-11',
          YELLOW: 'bg-n-amber-3 text-n-amber-11',
          RED: 'bg-n-ruby-3 text-n-ruby-11',
        }[quality] || 'bg-n-alpha-2 text-n-slate-11',
    };
  }
  if (inbox.channel_type === INBOX_TYPES.EMAIL) {
    return inbox.imap_enabled
      ? { label: listCopy.emailReceiving, tone: 'bg-n-teal-3 text-n-teal-11' }
      : {
          label: listCopy.emailNotReceiving,
          tone: 'bg-n-alpha-2 text-n-slate-11',
        };
  }
  return {
    label: listCopy.channelStatus,
    tone: 'bg-n-alpha-2 text-n-slate-11',
  };
};

// Shows the address customers use to reach this connection, when there is one.
const inboxIdentifier = inbox =>
  inbox.phone_number ||
  inbox.email ||
  inbox.website_url?.replace(/^https?:\/\//, '') ||
  '';

const canFetchHealthForInbox = inbox =>
  inbox.channel_type === INBOX_TYPES.WHATSAPP &&
  (inbox.provider === 'whatsapp_cloud' ||
    inbox.provider_config?.source === 'embedded_signup' ||
    inbox.provider_config?.phone_number_id);

const loadInboxBot = async inbox => {
  try {
    await store.dispatch('agentBots/fetchAgentBotInbox', inbox.id);
  } catch {
    // Caixa sem bot conectado ou endpoint indisponível.
  }
};

const loadInboxHealth = async inbox => {
  if (!canFetchHealthForInbox(inbox) || loadedHealthIds.has(inbox.id)) return;
  loadedHealthIds.add(inbox.id);

  try {
    const { data } = await InboxHealthAPI.getHealthStatus(inbox.id);
    healthByInbox.value = {
      ...healthByInbox.value,
      [inbox.id]: data,
    };
  } catch {
    healthByInbox.value = {
      ...healthByInbox.value,
      [inbox.id]: {},
    };
  }
};

const loadInboxExtras = async list => {
  if (!list?.length) return;

  list.forEach(inbox => {
    if (loadedInboxIds.has(inbox.id)) return;
    loadedInboxIds.add(inbox.id);
    loadInboxBot(inbox);
    loadInboxHealth(inbox);
  });
};

const groupedFilteredInboxes = computed(() => {
  const groups = filteredInboxesList.value.reduce((acc, inbox) => {
    const category = getInboxCategory(inbox);
    if (!acc[category]) acc[category] = [];
    acc[category].push(inbox);
    return acc;
  }, {});

  return categoryOrder
    .filter(category => groups[category]?.length)
    .map(category => ({
      key: category,
      title: categoryLabels[category],
      description: categoryDescriptions[category],
      icon: categoryIcons[category],
      inboxes: groups[category],
    }));
});

const totalConnections = computed(() => inboxesList.value?.length || 0);
const whatsappConnections = computed(
  () =>
    inboxesList.value?.filter(inbox => getInboxCategory(inbox) === 'whatsapp')
      .length || 0
);
const connectedAssistants = computed(
  () =>
    inboxesList.value?.filter(inbox => Boolean(activeBotForInbox(inbox)?.id))
      .length || 0
);

const buildTargetOptions = sourceInbox => {
  if (!sourceInbox?.id) return [];

  return inboxesList.value
    .filter(inbox => inbox.id !== sourceInbox.id)
    .map(inbox => ({
      id: inbox.id,
      name: inbox.name,
      channelType: inbox.channel_type,
      compatible: isMigrationCompatible(sourceInbox, inbox),
    }));
};

const migrationTargetOptions = computed(() =>
  buildTargetOptions(migrationSourceInbox.value)
);

const migrationTargetSelectOptions = computed(() =>
  migrationTargetOptions.value.map(targetInbox => ({
    value: String(targetInbox.id),
    label: targetInbox.compatible
      ? targetInbox.name
      : `${targetInbox.name} (incompatível)`,
    disabled: !targetInbox.compatible,
  }))
);

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const deleteTargetInboxOptions = computed(() =>
  buildTargetOptions(selectedInbox.value)
);

const deleteTargetSelectOptions = computed(() =>
  deleteTargetInboxOptions.value.map(targetInbox => ({
    value: String(targetInbox.id),
    label: targetInbox.compatible
      ? targetInbox.name
      : `${targetInbox.name} (incompatível)`,
    disabled: !targetInbox.compatible,
  }))
);

const isCompatibleTarget = (sourceInbox, targetInboxId) => {
  const selected = buildTargetOptions(sourceInbox).find(
    option => Number(option.id) === Number(targetInboxId)
  );

  return Boolean(selected?.compatible);
};

const deleteInbox = async ({ id }, deleteParams = {}) => {
  try {
    const result = await store.dispatch('inboxes/delete', { id, deleteParams });
    useAlert(
      result?.deletion_scheduled
        ? listCopy.deleteScheduledSuccess
        : t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE')
    );
    return true;
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE')
    );
    return false;
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const closeMigrate = () => {
  showMigratePopup.value = false;
  deleteMode.value = 'migrate';
  deleteTargetInboxId.value = '';
  migrationSourceInbox.value = {};
  migrationTargetInboxId.value = null;
};

const confirmMigration = async () => {
  const targetInboxId = Number(migrationTargetInboxId.value);
  if (Number.isNaN(targetInboxId) || targetInboxId <= 0) {
    useAlert(listCopy.migrateValidationError);
    return;
  }

  if (!isCompatibleTarget(migrationSourceInbox.value, targetInboxId)) {
    useAlert(listCopy.incompatibleTargetError);
    return;
  }

  try {
    isMigratingData.value = true;
    await store.dispatch('inboxes/migrateData', {
      sourceInboxId: migrationSourceInbox.value.id,
      targetInboxId,
    });
    useAlert(listCopy.migrateSuccess);
    closeMigrate();
  } catch {
    useAlert(listCopy.migrateError);
  } finally {
    isMigratingData.value = false;
  }
};

// Permanent deletion requires typing the inbox name, so a misclick cannot erase history.
const isHardDeleteConfirmed = computed(
  () =>
    deleteConfirmationName.value.trim() !== '' &&
    deleteConfirmationName.value.trim() ===
      String(selectedInbox.value?.name || '').trim()
);

const isDeleteConfirmDisabled = computed(() => {
  if (uiFlags.value.isDeleting) return true;
  if (deleteMode.value === 'hard_delete') return !isHardDeleteConfirmed.value;
  return !deleteTargetInboxId.value;
});

const confirmDeletion = async () => {
  if (uiFlags.value.isDeleting) return;
  if (deleteMode.value === 'hard_delete' && !isHardDeleteConfirmed.value) {
    return;
  }

  if (deleteMode.value === 'hard_delete') {
    const deleted = await deleteInbox(selectedInbox.value, {
      delete_all_contacts_and_messages: true,
    });
    if (deleted) closeDelete();
    return;
  }

  const targetInboxId = Number(deleteTargetInboxId.value);
  if (Number.isNaN(targetInboxId) || targetInboxId <= 0) {
    useAlert(listCopy.deleteValidationError);
    return;
  }

  if (!isCompatibleTarget(selectedInbox.value, targetInboxId)) {
    useAlert(listCopy.incompatibleTargetError);
    return;
  }

  const deleted = await deleteInbox(selectedInbox.value, {
    target_inbox_id: targetInboxId,
  });
  if (deleted) closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  deleteMode.value = 'migrate';
  deleteConfirmationName.value = '';
  deleteTargetInboxId.value = '';
  selectedInbox.value = inbox;
};

const getChannelIconFrame = inbox => useChannelIconFrame(inbox).value;

const hasInboxAvatar = inbox =>
  Boolean(inbox.avatar_url) && !failedInboxAvatarIds.value[inbox.id];

const markInboxAvatarAsFailed = inbox => {
  failedInboxAvatarIds.value = {
    ...failedInboxAvatarIds.value,
    [inbox.id]: true,
  };
};

const openInboxSettings = inbox =>
  router.push({
    name: 'settings_inbox_show',
    params: { inboxId: inbox.id },
  });

watch(
  inboxesList,
  list => {
    loadInboxExtras(list);
  },
  { immediate: true }
);

onMounted(async () => {
  try {
    // This screen must not depend on the cached request started by the sidebar.
    // A stalled browser cache/IndexedDB request used to leave it loading forever.
    await store.dispatch('inboxes/getFromNetwork');
  } catch {
    useAlert(t('INBOX_MGMT.LIST.FETCH_ERROR'));
  } finally {
    isLoadingInboxes.value = false;
  }

  loadInboxExtras(inboxesList.value);
});
</script>

<template>
  <SettingsLayout
    :no-records-found="!inboxesList.length"
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="isLoadingInboxes"
  >
    <template #header>
      <div class="flex flex-col gap-4">
        <section
          class="rounded-3xl border border-n-slate-5 dark:border-n-slate-7 bg-n-solid-1 p-5 shadow-sm sm:p-6"
        >
          <div
            class="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between"
          >
            <div class="flex min-w-0 items-start gap-4">
              <div
                class="grid size-12 shrink-0 place-items-center rounded-2xl bg-n-brand/10 text-n-brand"
              >
                <Icon icon="i-lucide-inbox" class="size-6" />
              </div>
              <div class="min-w-0">
                <p
                  class="mb-0 text-xs font-semibold uppercase tracking-[0.14em] text-n-brand"
                >
                  {{ listCopy.eyebrow }}
                </p>
                <h1
                  class="mb-0 mt-1 text-2xl font-semibold tracking-tight text-n-slate-12"
                >
                  {{ listCopy.overviewTitle }}
                </h1>
                <p
                  class="mb-0 mt-2 max-w-2xl text-sm leading-6 text-n-slate-11"
                >
                  {{ listCopy.overviewDescription }}
                </p>
              </div>
            </div>

            <router-link v-if="isAdmin" :to="{ name: 'settings_inbox_new' }">
              <Button
                :label="listCopy.newConnection"
                icon="i-lucide-plus-circle"
                size="md"
                class="shadow-md shadow-n-brand/20"
              />
            </router-link>
          </div>

          <div class="mt-6 grid gap-3 sm:grid-cols-3">
            <div
              class="flex items-center gap-3 rounded-2xl border border-n-slate-5 dark:border-n-slate-7 bg-n-alpha-1 px-4 py-3"
            >
              <Icon icon="i-lucide-network" class="size-5 text-n-slate-10" />
              <div>
                <strong class="block text-lg font-semibold text-n-slate-12">
                  {{ totalConnections }}
                </strong>
                <span class="text-xs text-n-slate-10">
                  {{ listCopy.totalConnections }}
                </span>
              </div>
            </div>
            <div
              class="flex items-center gap-3 rounded-2xl border border-n-slate-5 dark:border-n-slate-7 bg-n-alpha-1 px-4 py-3"
            >
              <Icon icon="i-brand-whatsapp" class="size-5 text-n-slate-10" />
              <div>
                <strong class="block text-lg font-semibold text-n-slate-12">
                  {{ whatsappConnections }}
                </strong>
                <span class="text-xs text-n-slate-10">
                  {{ listCopy.whatsappConnections }}
                </span>
              </div>
            </div>
            <div
              class="flex items-center gap-3 rounded-2xl border border-n-slate-5 dark:border-n-slate-7 bg-n-alpha-1 px-4 py-3"
            >
              <Icon icon="i-lucide-bot" class="size-5 text-n-slate-10" />
              <div>
                <strong class="block text-lg font-semibold text-n-slate-12">
                  {{ connectedAssistants }}
                </strong>
                <span class="text-xs text-n-slate-10">
                  {{ listCopy.connectedAssistants }}
                </span>
              </div>
            </div>
          </div>
        </section>

        <section
          class="flex flex-col gap-3 rounded-2xl border border-n-slate-5 dark:border-n-slate-7 bg-n-solid-1 p-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <div class="flex items-center gap-2 px-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ listCopy.searchLabel }}
            </span>
            <span
              class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs font-medium text-n-slate-10"
            >
              {{ totalConnections }}
            </span>
          </div>
          <label class="relative block w-full sm:max-w-sm">
            <span class="sr-only">{{ listCopy.searchLabel }}</span>
            <Icon
              icon="i-lucide-search"
              class="pointer-events-none absolute left-3 top-1/2 z-10 size-4 -translate-y-1/2 text-n-slate-10"
            />
            <Input
              v-model="searchQuery"
              type="search"
              size="md"
              :placeholder="$t('INBOX_MGMT.SEARCH_PLACEHOLDER')"
              custom-input-class="!rounded-xl !bg-n-alpha-1 ltr:!pl-9 rtl:!pr-9"
            />
          </label>
        </section>
      </div>
    </template>

    <template #body>
      <span
        v-if="!filteredInboxesList.length && searchQuery"
        class="flex flex-1 items-center justify-center rounded-2xl border border-dashed border-n-weak py-20 text-center text-base text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.NO_RESULTS') }}
      </span>
      <div v-else class="flex flex-col gap-8 pb-4">
        <section
          v-for="group in groupedFilteredInboxes"
          :key="group.key"
          class="flex flex-col gap-4"
        >
          <div class="flex items-center gap-3">
            <div
              class="grid size-9 shrink-0 place-items-center rounded-xl border border-n-weak bg-n-alpha-1 text-n-slate-11"
            >
              <Icon :icon="group.icon" class="size-4.5" />
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2">
                <h2 class="mb-0 text-base font-semibold text-n-slate-12">
                  {{ group.title }}
                </h2>
                <span
                  class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs font-medium text-n-slate-10"
                >
                  {{ group.inboxes.length }}
                </span>
              </div>
              <p class="mb-0 mt-0.5 text-sm text-n-slate-10">
                {{ group.description }}
              </p>
            </div>
          </div>

          <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            <article
              v-for="inbox in group.inboxes"
              :key="inbox.id"
              :class="[
                qualityClassForInbox(inbox),
                isAdmin
                  ? 'cursor-pointer hover:bg-n-alpha-1 hover:shadow-md'
                  : '',
              ]"
              class="group relative flex min-w-0 flex-col rounded-2xl border bg-n-solid-1 p-3 shadow-sm transition-colors"
            >
              <button
                v-if="isAdmin"
                type="button"
                class="absolute inset-0 cursor-pointer rounded-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                :aria-label="`Configurar: ${inbox.name}`"
                @click.stop="openInboxSettings(inbox)"
              />
              <div class="flex min-w-0 items-start gap-3">
                <span class="relative shrink-0">
                  <span
                    class="grid size-11 place-items-center overflow-hidden rounded-full border ring-2 ring-n-solid-1"
                    :class="getChannelIconFrame(inbox)"
                  >
                    <img
                      v-if="hasInboxAvatar(inbox)"
                      :src="inbox.avatar_url"
                      :alt="inbox.name"
                      class="size-full rounded-full object-cover"
                      @error="markInboxAvatarAsFailed(inbox)"
                    />
                    <ChannelIcon
                      v-else
                      colored
                      use-brand-icon
                      class="size-6"
                      :inbox="inbox"
                    />
                  </span>
                  <span
                    v-if="hasInboxAvatar(inbox)"
                    class="absolute -bottom-0.5 -right-0.5 grid size-5 place-items-center rounded-full border border-n-weak bg-n-solid-1"
                    aria-hidden="true"
                  >
                    <ChannelIcon
                      colored
                      use-brand-icon
                      class="size-3"
                      :inbox="inbox"
                    />
                  </span>
                </span>
                <div class="min-w-0 flex-1">
                  <span
                    class="block truncate text-sm font-semibold capitalize text-n-slate-12"
                    :title="inbox.name"
                  >
                    {{ inbox.name }}
                  </span>
                  <ChannelName
                    :channel-type="inbox.channel_type"
                    :medium="inbox.medium"
                    :voice-enabled="inbox.voice_enabled"
                    class="mt-0.5 text-xs text-n-slate-10"
                  />
                  <span
                    v-if="inboxIdentifier(inbox)"
                    class="mt-0.5 block truncate text-xs tabular-nums text-n-slate-11"
                    :title="inboxIdentifier(inbox)"
                  >
                    {{ inboxIdentifier(inbox) }}
                  </span>
                </div>
                <span
                  class="mt-1 size-2 shrink-0 rounded-full ring-4 ring-n-alpha-2"
                  :class="qualityDotClass(inbox)"
                  role="img"
                  :aria-label="
                    isWhatsAppLikeInbox(inbox)
                      ? qualityTextForInbox(inbox)
                      : listCopy.channelStatus
                  "
                  :title="
                    isWhatsAppLikeInbox(inbox)
                      ? qualityTextForInbox(inbox)
                      : listCopy.channelStatus
                  "
                />
              </div>

              <div class="mt-3 flex min-h-7 flex-wrap items-center gap-1.5">
                <span
                  class="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium"
                  :class="qualityBadgeForInbox(inbox).tone"
                >
                  {{ qualityBadgeForInbox(inbox).label }}
                </span>
                <span
                  class="inline-flex min-w-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs"
                  :class="
                    activeBotForInbox(inbox).name
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-alpha-1 text-n-slate-11'
                  "
                >
                  <Icon icon="i-hugeicons-robotic" class="size-3.5 shrink-0" />
                  <span class="truncate">{{ activeBotName(inbox) }}</span>
                </span>
              </div>

              <div
                v-if="isAdmin"
                class="mt-3 flex items-center justify-between border-t border-n-weak pt-2"
              >
                <span
                  class="inline-flex items-center gap-1 text-xs font-medium text-n-slate-11 transition-colors group-hover:text-n-brand"
                  aria-hidden="true"
                >
                  {{ listCopy.openSettings }}
                  <span class="i-hugeicons-arrow-right-01 size-3.5" />
                </span>
                <div class="relative z-10">
                  <Button
                    icon="i-lucide-trash-2"
                    color="ruby"
                    variant="ghost"
                    size="sm"
                    :aria-label="`Excluir: ${inbox.name}`"
                    @click.stop="openDelete(inbox)"
                  />
                </div>
              </div>
            </article>
          </div>
        </section>
      </div>
    </template>
    <woot-modal v-model:show="showDeletePopup" @close="closeDelete">
      <div class="flex flex-col gap-5 p-6">
        <header class="flex items-start gap-3 pe-8">
          <span
            class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-n-ruby-3 text-n-ruby-11"
          >
            <Icon icon="i-hugeicons-delete-02" class="size-5" />
          </span>
          <div class="min-w-0">
            <h2 class="mb-0 text-base font-semibold text-n-slate-12">
              {{ listCopy.deleteModalTitle }}
            </h2>
            <p class="mb-0 mt-1 text-sm leading-5 text-n-slate-11">
              {{ listCopy.deleteModalDescription }}
            </p>
          </div>
        </header>

        <div
          class="flex items-center gap-3 rounded-xl border border-n-weak bg-n-solid-2 px-3 py-2.5"
        >
          <span
            class="flex size-9 shrink-0 items-center justify-center rounded-full bg-n-solid-1 outline outline-1 outline-n-weak"
          >
            <ChannelIcon
              v-if="selectedInbox.id"
              :inbox="selectedInbox"
              colored
              use-brand-icon
              class="size-4"
            />
          </span>
          <div class="min-w-0">
            <p
              class="mb-0 text-[0.6875rem] font-semibold uppercase tracking-wide text-n-slate-10"
            >
              {{ listCopy.selectedInbox }}
            </p>
            <p
              class="mb-0 truncate text-sm font-semibold text-n-slate-12"
              :title="selectedInbox.name"
            >
              {{ selectedInbox.name || '-' }}
            </p>
          </div>
        </div>

        <fieldset class="m-0 flex flex-col gap-2 border-0 p-0">
          <legend class="mb-2 p-0 text-sm font-medium text-n-slate-12">
            {{ listCopy.deleteModeLabel }}
          </legend>

          <label
            class="inbox-delete-option"
            :class="{ 'is-selected': deleteMode === 'migrate' }"
          >
            <input
              v-model="deleteMode"
              type="radio"
              value="migrate"
              name="inbox_delete_mode"
              class="sr-only"
            />
            <span
              class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-n-alpha-2 text-n-slate-11"
            >
              <Icon
                icon="i-hugeicons-arrow-data-transfer-horizontal"
                class="size-4"
              />
            </span>
            <span class="min-w-0 flex-1">
              <span class="block text-sm font-medium text-n-slate-12">
                {{ listCopy.deleteModeMigrate }}
              </span>
              <span class="mt-0.5 block text-xs leading-5 text-n-slate-11">
                {{ listCopy.deleteModeMigrateHint }}
              </span>
            </span>
            <span class="inbox-delete-option__radio" aria-hidden="true" />
          </label>

          <label
            class="inbox-delete-option is-danger"
            :class="{ 'is-selected': deleteMode === 'hard_delete' }"
          >
            <input
              v-model="deleteMode"
              type="radio"
              value="hard_delete"
              name="inbox_delete_mode"
              class="sr-only"
            />
            <span
              class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-n-ruby-3 text-n-ruby-11"
            >
              <Icon icon="i-hugeicons-alert-02" class="size-4" />
            </span>
            <span class="min-w-0 flex-1">
              <span class="block text-sm font-medium text-n-ruby-11">
                {{ listCopy.deleteModeHardDelete }}
              </span>
              <span class="mt-0.5 block text-xs leading-5 text-n-slate-11">
                {{ listCopy.deleteModeHardDeleteHint }}
              </span>
            </span>
            <span class="inbox-delete-option__radio" aria-hidden="true" />
          </label>
        </fieldset>

        <div v-if="deleteMode === 'migrate'" class="flex flex-col gap-1.5">
          <label
            class="text-sm font-medium text-n-slate-12"
            for="delete_target_inbox"
          >
            {{ listCopy.deleteTargetLabel }}
          </label>
          <SelectInput
            id="delete_target_inbox"
            v-model="deleteTargetInboxId"
            class="!w-full"
            :placeholder="listCopy.deleteTargetPlaceholder"
            :options="deleteTargetSelectOptions"
          />
          <p class="mb-0 text-xs text-n-slate-10">
            {{ listCopy.deleteTargetHint }}
          </p>
        </div>

        <div
          v-else
          class="flex flex-col gap-3 rounded-xl border border-n-ruby-5 bg-n-ruby-2 p-3"
        >
          <div class="flex items-start gap-2">
            <Icon
              icon="i-hugeicons-alert-02"
              class="mt-0.5 size-4 shrink-0 text-n-ruby-11"
            />
            <div class="min-w-0">
              <p class="mb-0 text-sm font-semibold text-n-ruby-11">
                {{ listCopy.deleteHardWarningTitle }}
              </p>
              <p class="mb-0 mt-0.5 text-xs leading-5 text-n-slate-11">
                {{ listCopy.deleteHardWarning }}
                <strong class="break-words text-n-slate-12">
                  {{ selectedInbox.name }}
                </strong>
              </p>
            </div>
          </div>
          <Input
            v-model="deleteConfirmationName"
            :label="listCopy.deleteHardInputLabel"
            :placeholder="selectedInbox.name"
            autocomplete="off"
          />
        </div>

        <footer
          class="flex flex-col-reverse gap-2 border-t border-n-weak pt-4 sm:flex-row sm:items-center sm:justify-end"
        >
          <Button
            :label="listCopy.deleteCancel"
            type="button"
            faded
            slate
            sm
            :disabled="uiFlags.isDeleting"
            @click="closeDelete"
          />
          <Button
            :label="
              deleteMode === 'hard_delete'
                ? listCopy.deleteHardConfirm
                : listCopy.deleteConfirm
            "
            type="button"
            sm
            ruby
            :icon="
              deleteMode === 'hard_delete'
                ? 'i-hugeicons-delete-02'
                : 'i-hugeicons-arrow-data-transfer-horizontal'
            "
            :is-loading="uiFlags.isDeleting"
            :disabled="isDeleteConfirmDisabled"
            @click="confirmDeletion"
          />
        </footer>
      </div>
    </woot-modal>

    <woot-modal v-model:show="showMigratePopup" @close="closeMigrate">
      <div class="p-6 flex flex-col gap-5">
        <woot-modal-header
          :header-title="listCopy.migrateModalTitle"
          :header-content="listCopy.migrateModalDescription"
        />

        <div class="flex flex-col gap-2">
          <label
            class="text-sm font-medium text-n-slate-12"
            for="migration_target_inbox"
          >
            {{ listCopy.migrateTargetLabel }}
          </label>
          <SelectInput
            id="migration_target_inbox"
            v-model="migrationTargetInboxId"
            class="!w-full"
            :placeholder="listCopy.migrateTargetPlaceholder"
            :options="migrationTargetSelectOptions"
          />
        </div>

        <div class="flex flex-wrap items-center justify-end gap-2 pt-1">
          <Button
            :label="listCopy.migrateCancel"
            type="button"
            faded
            slate
            sm
            :disabled="isMigratingData"
            @click="closeMigrate"
          />
          <Button
            :label="listCopy.migrateConfirm"
            type="button"
            sm
            :is-loading="isMigratingData"
            :disabled="isMigratingData"
            @click="confirmMigration"
          />
        </div>
      </div>
    </woot-modal>
  </SettingsLayout>
</template>

<style scoped lang="scss">
.inbox-delete-option {
  @apply flex cursor-pointer items-start gap-3 rounded-xl border border-solid border-n-weak bg-n-solid-1 p-3 transition-colors hover:border-n-slate-7;

  &:has(input:focus-visible) {
    @apply outline outline-2 outline-offset-2 outline-n-brand;
  }

  &.is-selected {
    @apply border-n-brand bg-n-alpha-1;
  }

  &.is-danger.is-selected {
    @apply border-n-ruby-8 bg-n-ruby-2;
  }
}

.inbox-delete-option__radio {
  @apply mt-1 size-4 shrink-0 rounded-full border-2 border-solid border-n-slate-7;

  .is-selected & {
    @apply border-[5px] border-n-brand;
  }

  .is-danger.is-selected & {
    @apply border-n-ruby-9;
  }
}
</style>
